import { useLanguage } from '../../LanguageContext';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  Clock, Paperclip, Download, FileCheck, Check, X,
  RefreshCw, Printer, Mail, MessageSquare, MapPin,
  Package, FileText, Euro, Send, Truck, Receipt,
  ChevronDown, ChevronUp, AlertCircle, User
} from 'lucide-react';

// ─── Status vertaalhelper ─────────────────────────────────────────────────────
const vertaalStatus = (status, t) => {
  if (!status || !t) return status || '';
  const sl = status.toLowerCase();
  if (sl.includes('geoffreerd') || sl.includes('prijs')) return t('status_prijs_ontvangen');
  if (sl.includes('opdracht ontvangen')) return t('status_akkoord_gegeven');
  if (sl.includes('opdracht verzonden') || sl.includes('bevestigd')) return t('status_opdracht_bevestigd');
  if (sl.includes('onderweg')) return t('status_onderweg');
  if (sl.includes('afgeleverd')) return t('status_afgeleverd');
  if (sl.includes('afgerond')) return t('status_afgerond');
  if (sl.includes('factuur verstuurd')) return t('status_factuur_verstuurd');
  if (sl.includes('betaald')) return t('status_betaald');
  if (sl.includes('geannuleerd') || sl.includes('afgeschreven')) return t('status_geannuleerd');
  return status;
};

const G = {
  navy:'#1a364c', navyMid:'#24496a', gold:'#d4a848',
  white:'#ffffff', bg:'#f8fafc', border:'#e2e8f0',
  borderL:'#f1f5f9', txt:'#1e293b', txt2:'#64748b', txt3:'#94a3b8',
  green:'#16a34a', greenL:'#dcfce7',
  blue:'#2563eb', blueL:'#dbeafe',
  orange:'#ea580c', orangeL:'#fed7aa',
  red:'#dc2626', redL:'#fee2e2',
  purple:'#7c3aed', purpleL:'#ede9fe',
};

// Events die NIET getoond worden aan klant
const VERBORGEN_EVENTS = [
  'prijzen_aangevraagd', 'prijzen_binnen', // interne prijsonderhandeling
];

// Events die WEL getoond worden maar met gefilterde details
const FILTER_DETAILS = {
  'prijsopgave_verstuurd': (d) => ({ prijs: d?.klantprijs }),
  'klant_akkoord': (d) => d,
  'opdracht_verstuurd': () => null, // geen vervoerder tonen
  'transport_gestart': (d) => ({ van: d?.van, naar: d?.naar }),
  'transport_afgerond': (d) => ({ losplaats: d?.losplaats }),
  'factuur_aangemaakt': (d) => ({ factuurNr: d?.factuurNr, bedrag: d?.bedrag }),
  'factuur_betaald': (d) => ({ bedrag: d?.bedrag }),
};

// Event labels als functie om t() te kunnen gebruiken binnen componenten
const getEventLabel = (type, t) => ({
  'aanvraag_ingediend':   t('status_aanvraag_ingediend'),
  'prijsopgave_verstuurd': t('prijsopgave'),
  'klant_akkoord':        t('status_akkoord_gegeven'),
  'opdracht_verstuurd':   t('status_opdracht_bevestigd'),
  'tracker_gekoppeld':    t('tracker'),
  'transport_gestart':    t('status_onderweg'),
  'transport_onderweg':   t('status_onderweg'),
  'transport_afgerond':   t('status_afgerond'),
  'factuur_aangemaakt':   t('factuur'),
  'factuur_verstuurd':    t('status_factuur_verstuurd'),
  'factuur_betaald':      t('status_betaald'),
  'notitie_toegevoegd':   t('opmerkingen'),
  'document_geupload':    t('documenten'),
  'hub_passage':          t('hub_bereikt'),
})[type] || type;

const EVENT_ICONS = {
  'aanvraag_ingediend': FileText,
  'prijsopgave_verstuurd': Euro,
  'klant_akkoord': Check,
  'opdracht_verstuurd': Send,
  'tracker_gekoppeld': MapPin,
  'transport_gestart': Truck,
  'transport_onderweg': Truck,
  'transport_afgerond': Package,
  'factuur_aangemaakt': Receipt,
  'factuur_verstuurd': Receipt,
  'factuur_betaald': Receipt,
  'hub_passage': MapPin,
};

const EVENT_KLEUREN = {
  'aanvraag_ingediend': G.blue,
  'prijsopgave_verstuurd': G.orange,
  'klant_akkoord': G.green,
  'opdracht_verstuurd': G.navy,
  'transport_gestart': G.purple,
  'transport_onderweg': G.purple,
  'transport_afgerond': G.green,
  'factuur_aangemaakt': G.gold,
  'factuur_verstuurd': G.gold,
  'factuur_betaald': G.green,
  'hub_passage': '#0891b2',
};

function fmtDT(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('nl-NL', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }); }
  catch(e) { return s; }
}
function fmtD(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('nl-NL', { day:'2-digit', month:'2-digit', year:'numeric' }); }
  catch(e) { return s; }
}
function fmtSize(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b/1024).toFixed(0)} KB`;
  return `${(b/1048576).toFixed(1)} MB`;
}
function fmtPrijs(n) {
  if (!n) return '—';
  return `€ ${Number(n).toLocaleString('nl-NL', { minimumFractionDigits:2, maximumFractionDigits:2 })}`;
}

// ─── Tijdlijn ─────────────────────────────────────────────────────────────────

function InfoRij({
  label, value, accent }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display:'flex', gap:8, marginBottom:8, fontSize:13 }}>
      <span style={{ color:G.txt3, minWidth:140, flexShrink:0 }}>{label}</span>
      <span style={{ color: accent || G.txt, fontWeight: accent ? 600 : 400 }}>{value}</span>
    </div>
  );
}

function AanvraagDetail({
  aanvraag, event }) {
  const { t } = useLanguage();
  const goederen = [];
  for (let i = 1; i <= 5; i++) {
    if (aanvraag[`Goed${i}_Soort`]) {
      goederen.push({
        aantal: aanvraag[`Goed${i}_Aantal`],
        soort: aanvraag[`Goed${i}_Soort`],
        omschrijving: aanvraag[`Goed${i}_Omschrijving`],
        lengte: aanvraag[`Goed${i}_Lengte`],
        breedte: aanvraag[`Goed${i}_Breedte`],
        hoogte: aanvraag[`Goed${i}_Hoogte`],
        gewicht: aanvraag[`Goed${i}_Gewicht`],
        stackable: aanvraag[`Goed${i}_Stackable`],
      });
    }
  }

  const services = ['Klep','Express','Parcel','Groupage','LTL','FTL','ADR','Customs','Kooiaap','LastMile','Geconditioneerd']
    .filter(s => aanvraag[s] === 1 || aanvraag[s] === true || aanvraag[s] === 'true');

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      {/* Route */}
      <div style={{ background:G.bg, borderRadius:8, padding:14, border:`1px solid ${G.border}` }}>
        <div style={{ fontWeight:700, fontSize:12, color:G.navy, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Route</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <div>
            <div style={{ fontSize:11, color:G.txt3, marginBottom:4 }}>{t('laadplaats').toUpperCase()}</div>
            <div style={{ fontWeight:700, fontSize:14, color:G.navy }}>{aanvraag.LaadPlaats}</div>
            {aanvraag.LaadAdres && <div style={{ fontSize:12, color:G.txt2, marginTop:2 }}>{aanvraag.LaadAdres}</div>}
            {aanvraag.LaadDatum && <div style={{ fontSize:12, color:G.blue, marginTop:4, fontWeight:600 }}>{t('datum')}: {fmtD(aanvraag.LaadDatum)}</div>}
          </div>
          <div>
            <div style={{ fontSize:11, color:G.txt3, marginBottom:4 }}>{t('losplaats').toUpperCase()}</div>
            <div style={{ fontWeight:700, fontSize:14, color:G.navy }}>{aanvraag.LosPlaats}</div>
            {aanvraag.LosAdres && <div style={{ fontSize:12, color:G.txt2, marginTop:2 }}>{aanvraag.LosAdres}</div>}
            {aanvraag.LosDatum && <div style={{ fontSize:12, color:G.blue, marginTop:4, fontWeight:600 }}>{t('datum')}: {fmtD(aanvraag.LosDatum)}</div>}
          </div>
        </div>
      </div>

      {/* Goederen */}
      {goederen.length > 0 && (
        <div style={{ background:G.bg, borderRadius:8, padding:14, border:`1px solid ${G.border}` }}>
          <div style={{ fontWeight:700, fontSize:12, color:G.navy, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>{t('goederen')}</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ background:G.navy, color:'white' }}>
                {[t('aantal'),t('soort'),t('omschrijving'),t('afmetingen'),t('gewicht'),'Stapelbaar'].map((h,i) => (
                  <th key={i} style={{ padding:'6px 8px', textAlign:'left', fontWeight:600, fontSize:11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {goederen.map((g, i) => (
                <tr key={i} style={{ background: i%2===0 ? G.white : G.bg, borderBottom:`1px solid ${G.borderL}` }}>
                  <td style={{ padding:'7px 8px' }}>{g.aantal || '-'}</td>
                  <td style={{ padding:'7px 8px' }}>{g.soort || '-'}</td>
                  <td style={{ padding:'7px 8px' }}>{g.omschrijving || '-'}</td>
                  <td style={{ padding:'7px 8px' }}>{g.lengte && g.breedte && g.hoogte ? `${g.lengte}x${g.breedte}x${g.hoogte}m` : '-'}</td>
                  <td style={{ padding:'7px 8px' }}>{g.gewicht ? `${g.gewicht}kg` : '-'}</td>
                  <td style={{ padding:'7px 8px' }}>{g.stackable ? t('ja') || 'Ja' : t('nee') || 'Nee'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Services */}
      {services.length > 0 && (
        <div style={{ background:G.bg, borderRadius:8, padding:14, border:`1px solid ${G.border}` }}>
          <div style={{ fontWeight:700, fontSize:12, color:G.navy, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Specialiteiten</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {services.map(s => (
              <span key={s} style={{ background:G.navy, color:'white', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:600 }}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Opmerkingen */}
      {aanvraag.Opmerkingen && (
        <div style={{ background:'#fffbeb', borderRadius:8, padding:14, border:`1px solid #fde68a` }}>
          <div style={{ fontWeight:700, fontSize:12, color:'#92400e', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Opmerkingen</div>
          <div style={{ fontSize:13, color:G.txt }}>{aanvraag.Opmerkingen}</div>
        </div>
      )}

      {/* Event info */}
      {event && (
        <div style={{ fontSize:11, color:G.txt3, textAlign:'right' }}>
          Ingediend: {fmtDT(event.event_timestamp)}
          {event.event_user && event.event_user !== 'System' && ` · ${event.event_user}`}
        </div>
      )}
    </div>
  );
}

function PrijsDetail({
  aanvraag, event }) {
  const { t } = useLanguage();
  // Klantprijs uit event_details.klantprijs (gezet door dashboard bij versturen prijsopgave)
  const netto = (() => {
    if (event?.event_details?.klantprijs) return parseFloat(event.event_details.klantprijs);
    try {
      const kp = JSON.parse(aanvraag.KlantPrijzen || '{}');
      const v = aanvraag.GekozenVervoerder ? kp[aanvraag.GekozenVervoerder] : Object.values(kp)[0];
      return v ? parseFloat(v) : null;
    } catch(e) { return null; }
  })();
  const btw = netto ? Math.round(netto * 0.21 * 100) / 100 : null;
  const totaal = netto ? netto + btw : null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ background:G.bg, borderRadius:8, padding:14, border:`1px solid ${G.border}` }}>
        <div style={{ fontWeight:700, fontSize:12, color:G.navy, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>{t('prijsopgave')}</div>
        <InfoRij label={t('route')} value={`${aanvraag.LaadPlaats} → ${aanvraag.LosPlaats}`} />
        <InfoRij label={t('laaddatum')} value={fmtD(aanvraag.LaadDatum)} />
        <InfoRij label={t('losdatum')} value={fmtD(aanvraag.LosDatum)} />
        {netto ? (
          <>
            <div style={{ borderTop:`1px solid ${G.borderL}`, margin:'10px 0' }} />
            <InfoRij label={t('prijs_excl_btw')} value={fmtPrijs(netto)} />
            <InfoRij label="BTW (21%)" value={fmtPrijs(btw)} />
            <InfoRij label={`${t('totaal')} incl. BTW`} value={fmtPrijs(totaal)} accent={G.navy} />
          </>
        ) : (
          <div style={{ fontSize:12, color:G.txt3, fontStyle:'italic' }}>{t('prijs_niet_beschikbaar')}</div>
        )}
      </div>
      {event && (
        <div style={{ fontSize:11, color:G.txt3, textAlign:'right' }}>
          Verstuurd: {fmtDT(event.event_timestamp)}
        </div>
      )}
    </div>
  );
}

function Tijdlijn({
  events, aanvraag }) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(null);
  
  const gefilterd = (events || []).filter(e => !VERBORGEN_EVENTS.includes(e.event_type));
  
  if (gefilterd.length === 0) return (
    <div style={{ textAlign:'center', padding:40, color:G.txt3, fontSize:13 }}>
      {t('geen_events')}
    </div>
  );

  const renderDetail = (ev) => {
    if (ev.event_type === 'aanvraag_ingediend') {
      return <AanvraagDetail aanvraag={aanvraag} event={ev} />;
    }
    if (ev.event_type === 'prijsopgave_verstuurd') {
      return <PrijsDetail aanvraag={aanvraag} event={ev} />;
    }
    if (ev.event_type === 'klant_akkoord') {
      const netto = (() => {
        try {
          const kp = JSON.parse(aanvraag.KlantPrijzen || '{}');
          const v = aanvraag.GekozenVervoerder ? kp[aanvraag.GekozenVervoerder] : Object.values(kp)[0];
          return v ? parseFloat(v) : null;
        } catch(e) { return null; }
      })();
      const btw = netto ? Math.round(netto * 0.21 * 100) / 100 : null;
      const totaal = netto ? netto + btw : null;
      return (
        <div style={{ fontSize:13, color:G.txt }}>
          <InfoRij label={t('status_akkoord_gegeven')} value={fmtDT(ev.event_timestamp)} />
          {ev.event_user && ev.event_user !== 'System' && ev.event_user !== 'Dashboard' &&
            <InfoRij label={t('ontvangen_door')} value={ev.event_user} accent={G.navy} />}
          <InfoRij label="Route" value={`${aanvraag.LaadPlaats} → ${aanvraag.LosPlaats}`} />
          <InfoRij label="Laaddatum" value={fmtD(aanvraag.LaadDatum)} />
          <InfoRij label="Losdatum" value={fmtD(aanvraag.LosDatum)} />
          {netto && (
            <>
              <div style={{ borderTop:`1px solid ${G.borderL}`, margin:'8px 0' }} />
              <InfoRij label={t('prijs_excl_btw')} value={fmtPrijs(netto)} />
              <InfoRij label="BTW (21%)" value={fmtPrijs(btw)} />
              <InfoRij label={`${t('totaal')} incl. BTW`} value={fmtPrijs(totaal)} accent={G.navy} />
            </>
          )}
        </div>
      );
    }
    if (ev.event_type === 'opdracht_verstuurd') {
      return (
        <div style={{ fontSize:13, color:G.txt }}>
          <InfoRij label={t('status_opdracht_bevestigd')} value={fmtDT(ev.event_timestamp)} />
          <InfoRij label="Route" value={`${aanvraag.LaadPlaats} → ${aanvraag.LosPlaats}`} />
          <InfoRij label="Laaddatum" value={fmtD(aanvraag.LaadDatum)} />
          <InfoRij label="Losdatum" value={fmtD(aanvraag.LosDatum)} />
          {aanvraag.TrackerID && <InfoRij label="Tracker" value={aanvraag.TrackerID} accent={G.navy} />}
          {aanvraag.FactuurNr && <InfoRij label={t('factuurnummer')} value={aanvraag.FactuurNr} />}
        </div>
      );
    }
    if (ev.event_type === 'transport_gestart' || ev.event_type === 'transport_onderweg') {
      return (
        <div style={{ fontSize:13, color:G.txt }}>
          <InfoRij label={t('status_onderweg')} value={fmtDT(ev.event_timestamp)} />
          {aanvraag.TrackerID && <InfoRij label="Tracker" value={aanvraag.TrackerID} accent={G.navy} />}
          <InfoRij label={t('laadplaats')} value={aanvraag.LaadPlaats} />
          <InfoRij label={t('losplaats')} value={aanvraag.LosPlaats} />
          <InfoRij label={t('losdatum')} value={fmtD(aanvraag.LosDatum)} />
        </div>
      );
    }
    if (ev.event_type === 'transport_afgerond') {
      return (
        <div style={{ fontSize:13, color:G.txt }}>
          <InfoRij label={t('status_afgerond')} value={fmtDT(ev.event_timestamp)} />
          <InfoRij label={t('losplaats')} value={aanvraag.LosPlaats} />
          {ev.event_details?.losplaats && <InfoRij label={t('adres')} value={ev.event_details.losplaats} />}
        </div>
      );
    }
    if (ev.event_type === 'factuur_aangemaakt' || ev.event_type === 'factuur_verstuurd') {
      return (
        <div style={{ fontSize:13, color:G.txt }}>
          <InfoRij label={t('datum')} value={fmtDT(ev.event_timestamp)} />
          {aanvraag.FactuurNr && <InfoRij label="Factuurnummer" value={aanvraag.FactuurNr} accent={G.navy} />}
        </div>
      );
    }
    if (ev.event_type === 'hub_passage') {
      const d = ev.event_details || {};
      return (
        <div style={{ fontSize:13, color:G.txt }}>
          <InfoRij label={t('datum')} value={fmtDT(ev.event_timestamp)} />
          <InfoRij label={t('hub_bereikt')} value={d.hub || ev.related_document} />
          <InfoRij label={t('laadplaats')} value={[d.stad, d.land].filter(Boolean).join(', ')} />
          <InfoRij label={t('soort')} value={d.type} />
          <InfoRij label={t('actie')} value={d.actie} />
        </div>
      );
    }
    // Generieke details
    if (ev.event_details && typeof ev.event_details === 'object') {
      const filtered = Object.entries(ev.event_details).filter(([,v]) => v);
      if (filtered.length > 0) {
        return (
          <div style={{ fontSize:13 }}>
            {filtered.map(([k,v]) => <InfoRij key={k} label={k} value={String(v)} />)}
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div style={{ position:'relative', paddingLeft:36 }}>
      <div style={{ position:'absolute', left:13, top:8, bottom:8, width:2, background:G.border }} />
      {gefilterd.map((ev, i) => {
        const Icon = EVENT_ICONS[ev.event_type] || Clock;
        const kleur = EVENT_KLEUREN[ev.event_type] || G.txt2;
        const label = getEventLabel(ev.event_type, t);
        const isExp = expanded === i;
        const detail = renderDetail(ev);

        return (
          <div key={ev.id || i} style={{ position:'relative', marginBottom:14 }}>
            <div style={{
              position:'absolute', left:-36, top:0,
              width:26, height:26, borderRadius:'50%',
              background:kleur, display:'flex', alignItems:'center', justifyContent:'center',
              boxShadow:'0 2px 6px rgba(0,0,0,0.2)',
            }}>
              <Icon size={13} color="#fff" />
            </div>
            <div
              onClick={() => detail && setExpanded(isExp ? null : i)}
              style={{
                background:G.white, border:`1px solid ${G.border}`, borderRadius:8,
                padding:'10px 14px', cursor: detail ? 'pointer' : 'default',
                transition:'box-shadow 0.15s',
              }}
              onMouseEnter={e => { if(detail) e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; }}
            >
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13, color:G.txt }}>{label}</div>
                  <div style={{ fontSize:11, color:G.txt3, marginTop:2 }}>
                    {fmtDT(ev.event_timestamp)}
                    {ev.event_user && ev.event_user !== 'System' && ev.event_user !== 'Testdata Generator' &&
                      <span style={{ marginLeft:8 }}>· {ev.event_user}</span>}
                  </div>
                </div>
                {detail && (isExp
                  ? <ChevronUp size={14} color={G.txt3}/>
                  : <ChevronDown size={14} color={G.txt3}/>
                )}
              </div>
              {isExp && detail && (
                <div style={{ marginTop:14, paddingTop:12, borderTop:`1px solid ${G.borderL}` }}>
                  {detail}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── GPS Tracking kaart ───────────────────────────────────────────────────────
function TrackingKaart({
  aanvraag }) {
  const { t } = useLanguage();
  const [posities, setPosties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plaatsnaam, setPlaatsnaam] = useState(t('huidige_positie'));

  const trackerId = aanvraag?.TrackerID && aanvraag.TrackerID !== 'undefined' && aanvraag.TrackerID !== '' ? aanvraag.TrackerID : null;

  useEffect(() => {
    if (!trackerId) { setLoading(false); return; }
    const afgeleverd = ['Afgeleverd','Factuur verstuurd','Betaald'].includes(aanvraag?.Status || '');
    let url = `/api/customer/tracker-positions/${encodeURIComponent(trackerId)}?limit=5000`;
    if (!afgeleverd && aanvraag?.LaadDatum) {
      url += '&from=' + new Date(aanvraag.LaadDatum).toISOString();
    }
    fetch(url, { credentials:'include' })
      .then(r => r.json())
      .then(d => {
        const arr = Array.isArray(d) ? d : (d.positions || []);
        setPosties(arr.sort((a,b) => new Date(a.gps_timestamp) - new Date(b.gps_timestamp)));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackerId, aanvraag?.LaadDatum, aanvraag?.Status]);

  useEffect(() => {
    if (posities.length === 0) return;
    const last = posities[posities.length - 1];
    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${last.latitude}&lon=${last.longitude}&zoom=10&accept-language=nl`, {
      headers: { 'User-Agent': 'SendWell-Portal/1.0' }
    })
      .then(r => r.json())
      .then(d => {
        const naam = d.address?.city || d.address?.town || d.address?.village || d.address?.county || d.name || t('huidige_positie');
        const land = d.address?.country || '';
        setPlaatsnaam(land ? `${naam}, ${land}` : naam);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posities.length]);

  if (!trackerId) return (
    <div style={{ textAlign:'center', padding:40, color:G.txt3, fontSize:13 }}>
      {t('geen_tracker')}
    </div>
  );

  if (loading) return (
    <div style={{ textAlign:'center', padding:40, color:G.txt3, fontSize:13 }}>
      <RefreshCw size={18} style={{ animation:'spin 1s linear infinite', marginBottom:8 }} />
      <div>{t('gps_laden')}</div>
    </div>
  );

  if (posities.length === 0) return (
    <div style={{ textAlign:'center', padding:40, color:G.txt3, fontSize:13 }}>
      {t('geen_gps')}
    </div>
  );

  const lijn = posities.map(p => [p.latitude, p.longitude]);

  // AutoZoom: past kaart aan op alle GPS-punten
  const AutoZoom = () => {
    const map = useMap();
    useEffect(() => {
      if (lijn.length > 1) {
        map.fitBounds(L.latLngBounds(lijn), { padding: [30, 30], animate: false });
      } else if (lijn.length === 1) {
        map.setView(lijn[0], 8);
      }
    }, [map]);
    return null;
  };
  const eerste = lijn[0];
  const laatste = lijn[lijn.length - 1];
  const center = eerste;

  return (
    <div style={{ height:500, borderRadius:8, overflow:'hidden', border:`1px solid ${G.border}` }}>
      <MapContainer center={center} zoom={6} style={{ height:'100%', width:'100%' }} zoomControl={true}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
        <AutoZoom />
        <Polyline positions={lijn} color={G.navy} weight={3} opacity={0.8} dashArray="6 4" />
        <CircleMarker center={eerste} radius={8} fillColor={G.green} color="#fff" fillOpacity={1} weight={2}>
          <Tooltip permanent direction="top">📦 {aanvraag.LaadPlaats}</Tooltip>
        </CircleMarker>
        <CircleMarker center={laatste} radius={10} fillColor={G.gold} color="#fff" fillOpacity={1} weight={2}>
          <Tooltip permanent direction="top">📍 {plaatsnaam}</Tooltip>
        </CircleMarker>
      </MapContainer>
    </div>
  );
}

// ─── Communicatie ─────────────────────────────────────────────────────────────
function Communicatie({
  aanvraagId }) {
  const { t } = useLanguage();
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetch(`/api/customer/bordero/${encodeURIComponent(aanvraagId)}/communicatie`, { credentials:'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.communicatie) setComms(d.communicatie); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [aanvraagId]);

  if (loading) return <div style={{ textAlign:'center', padding:40, color:G.txt3 }}>{t('laden')}</div>;
  if (comms.length === 0) return (
    <div style={{ textAlign:'center', padding:40, color:G.txt3, fontSize:13 }}>
      {t('geen_communicatie')}
    </div>
  );

  const TYPE_STIJL = {
    email:    { icon: Mail,           kleur: G.blue,   label: 'Email' },
    chat:     { icon: MessageSquare,  kleur: G.purple,  label: t('nav_berichten') },
    notitie:  { icon: FileText,       kleur: G.txt2,    label: t('opmerkingen') },
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      {comms.map((c, i) => {
        const stijl = TYPE_STIJL[c.type] || TYPE_STIJL.notitie;
        const Icon = stijl.icon;
        const isExp = expanded === i;
        return (
          <div key={c.id || i} style={{
            background:G.white, border:`1px solid ${G.border}`, borderRadius:8, overflow:'hidden'
          }}>
            <div
              onClick={() => setExpanded(isExp ? null : i)}
              style={{ padding:'12px 14px', cursor:'pointer', display:'flex', alignItems:'center', gap:10 }}
            >
              <div style={{ width:32, height:32, borderRadius:'50%', background:stijl.kleur+'20', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Icon size={15} color={stijl.kleur} />
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:13, color:G.txt, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {c.subject || stijl.label}
                </div>
                <div style={{ fontSize:11, color:G.txt3, marginTop:1 }}>
                  {fmtDT(c.created_at)}
                  {c.from_address && <span style={{ marginLeft:6 }}>· Van: {c.from_address}</span>}
                </div>
              </div>
              {isExp ? <ChevronUp size={14} color={G.txt3}/> : <ChevronDown size={14} color={G.txt3}/>}
            </div>
            {isExp && (
              <div style={{ padding:'0 14px 14px', borderTop:`1px solid ${G.borderL}` }}>
                <div style={{ fontSize:13, color:G.txt, whiteSpace:'pre-wrap', marginTop:10, lineHeight:1.6 }}>
                  {c.content || t('geen_resultaten')}
                </div>
                {c.image_data && (
                  <img src={c.image_data} alt="bijlage" style={{ maxWidth:'100%', marginTop:10, borderRadius:6 }} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Documenten ───────────────────────────────────────────────────────────────
function Documenten({
  bijlagen, cmr, pod }) {
  const { t } = useLanguage();
  const heeftData = (bijlagen && bijlagen.length > 0) || cmr || pod;

  if (!heeftData) return (
    <div style={{ textAlign:'center', padding:40, color:G.txt3, fontSize:13 }}>
      {t('geen_documenten')}
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {/* Bijlagen */}
      {bijlagen && bijlagen.length > 0 && (
        <div>
          <div style={{ fontWeight:600, fontSize:13, color:G.txt, marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
            <Paperclip size={14} color={G.navy} /> Bijlagen ({bijlagen.length})
          </div>
          {bijlagen.map(b => (
            <div key={b.id} style={{
              display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
              background:G.white, border:`1px solid ${G.border}`, borderRadius:8, marginBottom:6
            }}>
              <FileText size={18} color={G.navy} style={{ flexShrink:0 }} />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:500, fontSize:13, color:G.txt, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {b.original_name || b.filename}
                </div>
                <div style={{ fontSize:11, color:G.txt3 }}>{fmtSize(b.file_size)} · {fmtDT(b.uploaded_at)}</div>
              </div>
              <a href={`/api/customer/bordero/bijlage/${b.id}`} download
                style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 12px', borderRadius:6, background:G.navy, color:G.white, fontSize:12, fontWeight:500, textDecoration:'none', flexShrink:0 }}>
                <Download size={12} /> Download
              </a>
            </div>
          ))}
        </div>
      )}

      {/* CMR */}
      {cmr && (
        <div style={{ background:G.white, border:`1px solid ${G.border}`, borderRadius:8, overflow:'hidden' }}>
          <div style={{ padding:'10px 14px', background:G.navy, color:G.white, fontWeight:600, fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
            <FileCheck size={14} /> CMR-vrachtbrief
          </div>
          <div style={{ padding:14 }}>
            {cmr.cmr_nummer && <div style={{ display:'flex', gap:12, marginBottom:8, fontSize:13 }}><span style={{ color:G.txt3, minWidth:120 }}>{t('cmr_nummer')}</span><span style={{ fontWeight:600 }}>{cmr.cmr_nummer}</span></div>}
            {cmr.cmr_data && typeof cmr.cmr_data === 'object' && Object.entries(cmr.cmr_data)
              .filter(([k]) => !k.toLowerCase().includes('vervoerder') && !k.toLowerCase().includes('inkoop'))
              .map(([k,v]) => v ? (
                <div key={k} style={{ display:'flex', gap:12, marginBottom:8, fontSize:13 }}>
                  <span style={{ color:G.txt3, minWidth:120 }}>{k}</span>
                  <span style={{ color:G.txt }}>{String(v)}</span>
                </div>
              ) : null)}
          </div>
        </div>
      )}

      {/* POD */}
      {pod && (
        <div style={{ background:G.white, border:`1px solid ${G.border}`, borderRadius:8, overflow:'hidden' }}>
          <div style={{ padding:'10px 14px', background:G.green, color:G.white, fontWeight:600, fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
            <Check size={14} /> {t('proof_of_delivery')}
          </div>
          <div style={{ padding:14 }}>
            {pod.ontvanger && <div style={{ display:'flex', gap:12, marginBottom:8, fontSize:13 }}><span style={{ color:G.txt3, minWidth:120 }}>{t('ontvangen_door')}</span><span style={{ fontWeight:600 }}>{pod.ontvanger}</span></div>}
            {pod.ontvangst_datum && <div style={{ display:'flex', gap:12, marginBottom:8, fontSize:13 }}><span style={{ color:G.txt3, minWidth:120 }}>Datum</span><span>{fmtDT(pod.ontvangst_datum)}</span></div>}
            {pod.opmerkingen && <div style={{ display:'flex', gap:12, marginBottom:8, fontSize:13 }}><span style={{ color:G.txt3, minWidth:120 }}>Opmerkingen</span><span>{pod.opmerkingen}</span></div>}
            <div style={{ marginTop:8, padding:'8px 12px', background:G.greenL, borderRadius:6, fontSize:12, color:G.green, fontWeight:500 }}>
              {t('succesvol_afgeleverd')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Print ────────────────────────────────────────────────────────────────────
async function printDossier(aanvraag) {
  try {
    const res = await fetch(`/api/customer/bordero/${encodeURIComponent(aanvraag.AanvraagID)}/rapport`, { credentials:'include' });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } else {
      alert('Rapport kon niet worden gegenereerd. Probeer het opnieuw.');
    }
  } catch(e) {
    alert('Verbindingsfout bij genereren rapport.');
  }
}

// ─── Factuur tab ─────────────────────────────────────────────────────────────
function FactuurTab({
  aanvraag }) {
  const { t } = useLanguage();
  const verkoopPrijs = (() => {
    try {
      const kp = JSON.parse(aanvraag.KlantPrijzen || '{}');
      const v = aanvraag.GekozenVervoerder ? kp[aanvraag.GekozenVervoerder] : Object.values(kp)[0];
      return v ? parseFloat(v) : null;
    } catch(e) { return null; }
  })();
  const btw = verkoopPrijs ? verkoopPrijs * 0.21 : null;
  const totaal = verkoopPrijs ? verkoopPrijs + btw : null;

  if (!aanvraag.FactuurNr && !verkoopPrijs) return (
    <div style={{ textAlign:'center', padding:40, color:G.txt3, fontSize:13 }}>
      {t('geen_factuur')}
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      {aanvraag.FactuurNr && (
        <div style={{ background:G.white, border:`1px solid ${G.border}`, borderRadius:8, overflow:'hidden' }}>
          <div style={{ padding:'10px 14px', background:G.navy, color:G.white, fontWeight:600, fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
            <Receipt size={14} /> {t('factuur')}
          </div>
          <div style={{ padding:16 }}>
            {[
              [t('factuurnummer'), aanvraag.FactuurNr],
              [t('aanvraag'), aanvraag.AanvraagID],
              [t('route'), `${aanvraag.LaadPlaats} → ${aanvraag.LosPlaats}`],
            ].map(([k,v]) => (
              <div key={k} style={{ display:'flex', gap:12, marginBottom:10, fontSize:13 }}>
                <span style={{ color:G.txt3, minWidth:130 }}>{k}</span>
                <span style={{ color:G.txt, fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {verkoopPrijs && (
        <div style={{ background:G.white, border:`1px solid ${G.border}`, borderRadius:8, overflow:'hidden' }}>
          <div style={{ padding:'10px 14px', background:G.gold, color:G.white, fontWeight:600, fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
            <Euro size={14} /> Prijsoverzicht
          </div>
          <div style={{ padding:16 }}>
            {[
              [t('prijs_excl_btw'), fmtPrijs(verkoopPrijs)],
              ['BTW (21%)', fmtPrijs(btw)],
              [t('totaal') + ' incl. BTW', fmtPrijs(totaal)],
            ].map(([k,v], i) => (
              <div key={k} style={{ display:'flex', gap:12, marginBottom:10, fontSize:13, ...(i===2 ? { fontWeight:700, borderTop:`1px solid ${G.border}`, paddingTop:10 } : {}) }}>
                <span style={{ color:G.txt3, minWidth:130 }}>{k}</span>
                <span style={{ color: i===2 ? G.navy : G.txt, fontWeight: i===2 ? 700 : 500 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {aanvraag.FactuurNr && (
        <a href={`/api/customer/factuur-pdf/${aanvraag.FactuurNr}`} target="_blank" rel="noreferrer"
          style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px', background:G.navy, color:G.white, borderRadius:8, textDecoration:'none', fontWeight:600, fontSize:13 }}>
          <Download size={14} /> {t('downloaden')} ({aanvraag.FactuurNr})
        </a>
      )}
    </div>
  );
}

// ─── Draggable wrapper ───────────────────────────────────────────────────────
function DraggableWrapper({ children }) {
  const { t } = useLanguage();
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const dragging = React.useRef(false);
  const startMouse = React.useRef({ x:0, y:0 });
  const startOffset = React.useRef({ x:0, y:0 });

  const onMouseDown = (e) => {
    if (e.target.closest('button, a, input, select, .leaflet-container')) return;
    dragging.current = true;
    startMouse.current = { x: e.clientX, y: e.clientY };
    startOffset.current = { ...offset };
    e.preventDefault();
  };

  React.useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const dx = e.clientX - startMouse.current.x;
      const dy = e.clientY - startMouse.current.y;
      setOffset({ x: startOffset.current.x + dx, y: startOffset.current.y + dy });
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16, pointerEvents:'none' }}>
      <div
        onMouseDown={onMouseDown}
        style={{
          pointerEvents:'all',
          transform: `translate(${offset.x}px, ${offset.y}px)`,
          width:'100%', maxWidth:960,
          cursor: dragging.current ? 'grabbing' : 'default',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Hoofd component ──────────────────────────────────────────────────────────
const TABS = [
  { id:'tijdlijn',      labelKey:'tijdlijn',      icon:Clock },
  { id:'tracking',      labelKey:'route_gps',     icon:MapPin },
  { id:'communicatie',  labelKey:'communicatie',  icon:Mail },
  { id:'documenten',    labelKey:'documenten',    icon:Paperclip },
  { id:'factuur',       labelKey:'factuur',       icon:Receipt },
];

export default function CustomerBordero({ aanvraag, onClose }) {
  const { t } = useLanguage();
  const [tab, setTab] = useState('tijdlijn');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const laad = useCallback(async () => {
    if (!aanvraag?.AanvraagID) return;
    setLoading(true); setError(null);
    try {
      const r = await fetch(`/api/customer/bordero/${encodeURIComponent(aanvraag.AanvraagID)}`, { credentials:'include' });
      const d = await r.json();
      if (d.success) setData(d);
      else setError(d.error || t('laden') + '...');
    } catch(e) { setError('Verbindingsfout'); }
    finally { setLoading(false); }
  }, [aanvraag?.AanvraagID]);

  useEffect(() => { laad(); }, [laad]);

  if (!aanvraag) return null;

  const verkoopPrijs = (() => {
    try {
      const kp = JSON.parse(aanvraag.KlantPrijzen || '{}');
      return kp[aanvraag.GekozenVervoerder] ? parseFloat(kp[aanvraag.GekozenVervoerder]) : null;
    } catch(e) { return null; }
  })();

  return (
    <DraggableWrapper>
      <div style={{ background:G.bg, borderRadius:12, width:'100%', maxWidth:960, maxHeight:'95vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.35)' }}>
        
        {/* Header */}
        <div style={{ padding:'18px 22px', background:`linear-gradient(135deg, ${G.navy}, ${G.navyMid})`, borderRadius:'12px 12px 0 0', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
            <div>
              <div style={{ color:G.gold, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:1.5, marginBottom:4 }}>
                {t('zendingdossier')}
              </div>
              <div style={{ color:G.white, fontSize:18, fontWeight:700 }}>
                {aanvraag.AanvraagID}
              </div>
              <div style={{ color:'rgba(255,255,255,0.75)', fontSize:13, marginTop:3 }}>
                {aanvraag.LaadPlaats} → {aanvraag.LosPlaats}
                <span style={{ margin:'0 8px', opacity:0.4 }}>·</span>
                {fmtD(aanvraag.LaadDatum)} – {fmtD(aanvraag.LosDatum)}
              </div>
            </div>
            <div style={{ display:'flex', gap:8, flexShrink:0 }}>
              <button onClick={() => printDossier(aanvraag)} title="Afdrukken"
                style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:7, padding:'7px 10px', cursor:'pointer', color:G.white, display:'flex', alignItems:'center', gap:5, fontSize:12 }}>
                <Printer size={14} /> Print
              </button>
              <button onClick={laad} title="Verversen"
                style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:7, padding:'7px 10px', cursor:'pointer', color:G.white }}>
                <RefreshCw size={14} />
              </button>
              <button onClick={onClose}
                style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:7, padding:'7px 10px', cursor:'pointer', color:G.white }}>
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Info balk */}
          <div style={{ display:'flex', gap:16, marginTop:14, flexWrap:'wrap' }}>
            {[
              [t('status'), vertaalStatus(aanvraag.Status, t)],
              [t('goederen'), aanvraag.Goed1_Soort && `${aanvraag.Goed1_Aantal}x ${aanvraag.Goed1_Soort}`],
              verkoopPrijs ? [t('prijs'), fmtPrijs(verkoopPrijs)] : null,
              aanvraag.TrackerID ? [t('tracker'), aanvraag.TrackerID] : null,
            ].filter(Boolean).map(([k, v]) => v ? (
              <div key={k} style={{ background:'rgba(255,255,255,0.1)', borderRadius:6, padding:'5px 10px' }}>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.55)', textTransform:'uppercase', letterSpacing:1 }}>{k}</div>
                <div style={{ fontSize:12, color:G.white, fontWeight:600, marginTop:1 }}>{v}</div>
              </div>
            ) : null)}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', background:G.white, borderBottom:`1px solid ${G.border}`, flexShrink:0 }}>
          {TABS.map(tabItem => {
            const Icon = tabItem.icon;
            const actief = tab === tabItem.id;
            let badge = 0;
            if (tabItem.id === 'tijdlijn' && data?.events) badge = data.events.filter(e => !VERBORGEN_EVENTS.includes(e.event_type)).length;
            if (tabItem.id === 'documenten' && data) badge = (data.bijlagen?.length || 0) + (data.cmr ? 1 : 0) + (data.pod ? 1 : 0);
            return (
              <button key={tabItem.id} onClick={() => setTab(tabItem.id)} style={{
                flex:1, padding:'12px 6px', border:'none',
                borderBottom:`3px solid ${actief ? G.navy : 'transparent'}`,
                background:'none', cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:5,
                color: actief ? G.navy : G.txt2, fontWeight: actief ? 600 : 400, fontSize:12,
              }}>
                <Icon size={13} />
                {t(tabItem.labelKey)}
                {badge > 0 && (
                  <span style={{ background: actief ? G.navy : G.border, color: actief ? G.white : G.txt2, borderRadius:10, padding:'1px 5px', fontSize:10, fontWeight:600 }}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div style={{ flex:1, overflowY:'auto', padding:20 }}>
          {loading && (
            <div style={{ textAlign:'center', padding:48, color:G.txt3 }}>
              <RefreshCw size={22} style={{ animation:'spin 1s linear infinite', marginBottom:10 }} />
              <div>{t('dossier_laden')}</div>
            </div>
          )}
          {error && (
            <div style={{ textAlign:'center', padding:32, color:G.red }}>
              <AlertCircle size={22} style={{ marginBottom:8 }} /><div>{error}</div>
            </div>
          )}
          {!loading && !error && (
            <>
              {tab === 'tijdlijn' && <Tijdlijn events={data?.events} aanvraag={aanvraag} />}
              {tab === 'tracking' && <TrackingKaart aanvraag={aanvraag} />}
              {tab === 'communicatie' && <Communicatie aanvraagId={aanvraag.AanvraagID} />}
              {tab === 'documenten' && <Documenten bijlagen={data?.bijlagen} cmr={data?.cmr} pod={data?.pod} />}
              {tab === 'factuur' && <FactuurTab aanvraag={aanvraag} />}
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </DraggableWrapper>
  );
}
